import { PlusCircleOutlined } from "@ant-design/icons";
import { UserOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";
import { Message, MessageList, SenderInfo } from "@buzzeasy/shared-frontend-utilities/components";
import { FloatButton, theme } from "antd";
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Participant } from "../../model/hubDtos";
import { TranscriptHubContext } from "../providers/TranscriptHubProvider";
import InputContainer, { InputInterfaceRef } from "./InputContainer";
import SuggestedAnswerContainer from "./SuggestedAnswerContainer";
import TranscriptContainerLayout from "./TranscriptContainer.Layout";
import { getAgentColors, getCustomerColors, intermediateTranscriptToMessage, transcriptItemToMessage } from "./TranscriptContainer.utils";

const { useToken } = theme;

export default function TranscriptContainer(): ReactElement {
  const { transcriptItems, intermediateTranscriptItem, mode } = useContext(TranscriptHubContext);
  const { token } = useToken();
  const { t } = useTranslation();

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [showQuickResponses, setShowQuickResponses] = useState(true);
  const [showSuggestedAnswer, setShowSuggestedAnswer] = useState(true);

  const isScrolledToBottomRef = useRef(isScrolledToBottom);
  isScrolledToBottomRef.current = isScrolledToBottom;

  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<InputInterfaceRef>(null); // TODO: at a later point remove the inputRef prop-drill and introduce a context for the input value instead

  const senders = useMemo<Record<Participant, SenderInfo>>(
    () => ({
      Agent: {
        name: t("terms.agent").capitalize(),
        position: "right",
        avatar: { icon: <UserOutlined /> },
        colors: getAgentColors(token),
      },
      Customer: {
        name: t("terms.customer").capitalize(),
        position: "left",
        avatar: { icon: <UserOutlined /> },
        colors: getCustomerColors(token),
      },
    }),
    [t, token],
  );

  const messages = useMemo(
    () => {
      const items: Message<string, Participant>[] = transcriptItems.map(item => transcriptItemToMessage(item, token, mode));

      if (intermediateTranscriptItem)
        items.push(intermediateTranscriptToMessage(intermediateTranscriptItem));

      return items;
    },
    [intermediateTranscriptItem, token, transcriptItems, mode],
  );

  useEffect(
    () => {
      const content = contentRef.current;

      if (!content)
        return;

      const getIsScrolledToBottom = () => Math.abs(content.scrollHeight - content.scrollTop - content.clientHeight) < 2; // 2px tolerance
      const handleScroll = () => setIsScrolledToBottom(getIsScrolledToBottom());
      const handleResize = () => { if (isScrolledToBottomRef.current) content.scrollTop = content.scrollHeight; };

      content.addEventListener("scroll", handleScroll);
      const observer = new ResizeObserver(handleResize);
      observer.observe(content);

      return () => {
        content.removeEventListener("scroll", handleScroll);
        observer.disconnect();
      };
    },
    [],
  );

  useEffect(
    () => {
      if (contentRef.current && isScrolledToBottom) {
        contentRef.current.scrollTop = contentRef.current.scrollHeight;
      }
    },
    [isScrolledToBottom, transcriptItems],
  );

  return (
    <TranscriptContainerLayout
      contentRef={contentRef}
      scrollToBottomButton={
        <StyledFloatButton
          $visible={!isScrolledToBottom}
          type="primary"
          aria-label={t("transcript.goToRecentMessages")}
          icon={<VerticalAlignBottomOutlined />}
          onClick={() => { if (contentRef.current) contentRef.current.scrollTop = contentRef.current.scrollHeight; }}
          style={{ position: "unset" }}
        />
      }
      header={undefined}
      content={
        transcriptItems.length === 0
          ? <NoMessagesHint>{t("transcript.waitingForMessages")}</NoMessagesHint>
          : <StyledMessageList
              avatarPosition="bottom"
              messages={messages}
              senders={senders}
            />
      }
      suggestion={
        mode === "transcription"
          ? <>
              <SuggestedAnswerContainer
                onEditBeforeSend={answer => inputRef.current?.setInputTextAndFocus(answer)}
                showQuickResponses={false}
                showSuggestedAnswer={showSuggestedAnswer}
                onHideSuggestedAnswer={() => setShowSuggestedAnswer(false)}
                onHideQuickResponses={() => setShowQuickResponses(false)}
              />
              <TranscriptionModeFooter>
                <SuggestedAnswerFloatButton
                  $visible={!showSuggestedAnswer}
                  aria-label={t("agentAssist.showSuggestedAnswer")}
                  tooltip={t("agentAssist.showSuggestedAnswer")}
                  icon={<PlusCircleOutlined />}
                  onClick={() => setShowSuggestedAnswer(true)}
                />
              </TranscriptionModeFooter>
            </>
          : <SuggestedAnswerContainer
              onEditBeforeSend={answer => inputRef.current?.setInputTextAndFocus(answer)}
              showQuickResponses={mode === "translation" ? showQuickResponses : false}
              showSuggestedAnswer={showSuggestedAnswer}
              onHideSuggestedAnswer={() => setShowSuggestedAnswer(false)}
              onHideQuickResponses={() => setShowQuickResponses(false)}
            />
      }
      footer={
        mode === "translation"
          ? <InputContainer
              componentRef={inputRef}
              showQuickResponses={showQuickResponses}
              onShowQuickResponses={() => setShowQuickResponses(true)}
              showSuggestedAnswer={showSuggestedAnswer}
              onShowSuggestedAnswer={() => setShowSuggestedAnswer(true)}
            />
          : null
      }
    />
  );
}

const StyledMessageList = styled(MessageList)`
  margin-top: ${p => p.theme.paddingXXS}px;

  .be-MessageItem-ExtraContentBox {
    flex: 0 0 auto;
  }
`;

const NoMessagesHint = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: ${p => p.theme.padding}px;
  color: ${p => p.theme.colorTextPlaceholder};
  font-style: italic;
`;

const StyledFloatButton = styled(FloatButton)<{ $visible: boolean }>`
  @keyframes ScrollToBottomButton-fadeIn {
    from {
      display: none;
      opacity: 0;
    }
    to {
      display: unset;
      opacity: 1;
    }
  }

  @keyframes ScrollToBottomButton-fadeOut {
    from {
      display: unset;
      opacity: 1;
    }
    to {
      display: none;
      opacity: 0;
    }
  }

  animation: ScrollToBottomButton-${p => p.$visible ? "fadeIn" : "fadeOut"} ${p => p.theme.motionDurationMid} ${p => p.theme.motionEaseInOut};
  animation-fill-mode: both;
`;

const TranscriptionModeFooter = styled.div`
  display: flex;
  align-items: center;
  padding: ${p => p.theme.paddingXXS}px;
`;

const SuggestedAnswerFloatButton = styled(StyledFloatButton)`
  background-color: ${p => p.theme.colorBgContainer};
  color: ${p => p.theme.colorText};
`;
